/**
 * Decimal → human-friendly fraction conversion for recipe quantities.
 *
 * Rules (see project knowledge / FRACTION DISPLAY RULE):
 * - Never display decimals for cooking quantities.
 * - Snap to common cooking fractions: 1/8, 1/4, 1/3, 1/2, 2/3, 3/4.
 * - Use mixed numbers (e.g. "1 1/2", not "3/2").
 * - If the decimal does not snap cleanly, fall back to the nearest common fraction.
 */

const COMMON_FRACTIONS: { value: number; label: string }[] = [
  { value: 1 / 8, label: "1/8" },
  { value: 1 / 4, label: "1/4" },
  { value: 1 / 3, label: "1/3" },
  { value: 1 / 2, label: "1/2" },
  { value: 2 / 3, label: "2/3" },
  { value: 3 / 4, label: "3/4" },
];

const SNAP_TOLERANCE = 0.04; // ~ snaps 0.33 → 1/3, 0.67 → 2/3, etc.

const nearestFraction = (frac: number): string | null => {
  if (frac <= 0.0001) return null;
  if (frac >= 1 - 0.0001) return null;
  let best = COMMON_FRACTIONS[0];
  let bestDiff = Math.abs(frac - best.value);
  for (const f of COMMON_FRACTIONS) {
    const d = Math.abs(frac - f.value);
    if (d < bestDiff) {
      best = f;
      bestDiff = d;
    }
  }
  return best.label;
};

/** Convert a single numeric token (e.g. "0.5", "1.25") to a fraction string. */
export const decimalToFraction = (input: string | number): string => {
  const n = typeof input === "number" ? input : Number(String(input).replace(",", "."));
  if (!Number.isFinite(n)) return String(input);
  if (n < 0) return `-${decimalToFraction(-n)}`;

  const whole = Math.floor(n);
  const frac = n - whole;

  // Effectively whole number.
  if (frac < 0.02) return String(whole);
  // Rounds up to next whole.
  if (frac > 0.98) return String(whole + 1);

  // Try snapping to common fractions within tolerance.
  let label: string | null = null;
  let bestDiff = Infinity;
  for (const f of COMMON_FRACTIONS) {
    const d = Math.abs(frac - f.value);
    if (d < bestDiff) {
      bestDiff = d;
      label = f.label;
    }
  }
  if (bestDiff > SNAP_TOLERANCE) {
    // Fall back to nearest common fraction anyway (rule 5).
    label = nearestFraction(frac);
  }
  if (!label) return String(n);

  return whole > 0 ? `${whole} ${label}` : label;
};

/**
 * Replace every decimal number in a string with its fractional equivalent.
 * Leaves integers, fraction strings ("1/2"), and other text untouched.
 */
export const formatFractionsInText = (text: string): string => {
  if (!text) return text;
  // Match decimals like 0.5, .5, 1.25, 2,5 — but skip if followed by another digit
  // (avoids matching things inside "1.2.3" oddly). Also avoid things like "v1.2".
  return text.replace(/(?<![\d.])(\d*[.,]\d+)(?!\d*[.,]\d)/g, (match) => {
    // Preserve original if it has more than one decimal separator.
    return decimalToFraction(match);
  });
};
